import React, { useEffect, useState, useRef } from 'react';
import AppNav from '../components/AppNav';
import { apiClient } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_PHOTO_SIZE_BYTES, MAX_PHOTO_SIZE_MB, MIN_AGE, MAX_AGE, MAX_INTERESTS, PREFERENCE_OPTIONS } from '../constants';
import { compressImage } from '../lib/compressImage';
import GalleryEditor from '../components/GalleryEditor';
import PushToggle from '../components/PushToggle';
import CompleteProfileNudge from '../components/CompleteProfileNudge';
import EmailVerifyBanner from '../components/EmailVerifyBanner';
import BoostButton from '../components/BoostButton';
import ProfileCompletionScore from '../components/ProfileCompletionScore';
import PublicProfileShare from '../components/PublicProfileShare';
import ReferralsCard from '../components/ReferralsCard';

const MyProfile = () => {
  const { refresh } = useAuth();
  const fileRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('female');
  const [relationshipStatus, setRelationshipStatus] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [interests, setInterests] = useState([]);
  const [interestInput, setInterestInput] = useState('');
  const [preferences, setPreferences] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [publicProfile, setPublicProfile] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/profile/me');
        const p = res.data;
        if (p) {
          setName(p.name); setAge(p.age); setGender(p.gender);
          setRelationshipStatus(p.relationship_status || '');
          setBio(p.bio || ''); setLocation(p.location || '');
          setInterests(p.interests || []); setPhoto(p.photo_base64 || null);
          setPreferences(p.preferences || []);
          setGallery(p.gallery_photos || []);
          setPublicProfile(!!p.public_profile);
        }
      } catch (e) { console.error(e); }
      setLoaded(true);
    })();
  }, []);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      toast.error('Image too large', { description: `Under ${MAX_PHOTO_SIZE_MB}MB please.` }); return;
    }
    try { setPhoto(await compressImage(file)); }
    catch { toast.error("Couldn't read that image."); }
  };

  const addInterest = () => {
    const v = interestInput.trim();
    if (!v || interests.includes(v) || interests.length >= MAX_INTERESTS) return;
    setInterests([...interests, v]); setInterestInput('');
  };
  const removeInterest = (i) => setInterests(interests.filter((x) => x !== i));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/profile', {
        name: name.trim(), age: parseInt(age, 10), gender,
        relationship_status: relationshipStatus || null,
        bio: bio.trim(), location: location.trim(),
        interests, preferences, photo_base64: photo, gallery_photos: gallery,
        public_profile: publicProfile,
      });
      await refresh();
      toast.success('Profile updated');
    } catch (err) {
      toast.error('Save failed', { description: err.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  // Persist the public_profile toggle immediately on change so the user
  // doesn't have to scroll down + hit Save for a one-click toggle.
  const togglePublicProfile = async (next) => {
    setPublicProfile(next);
    try {
      await apiClient.put('/profile', {
        name: name.trim(), age: parseInt(age, 10), gender,
        relationship_status: relationshipStatus || null,
        bio: bio.trim(), location: location.trim(),
        interests, preferences, photo_base64: photo, gallery_photos: gallery,
        public_profile: next,
      });
      await refresh();
    } catch (err) {
      setPublicProfile(!next);
      toast.error('Could not save', { description: err.response?.data?.detail || '' });
    }
  };

  if (!loaded) return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-4 border-fnl-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-2xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-white mb-6">My Profile</h1>
        <EmailVerifyBanner />
        <CompleteProfileNudge context="views" />
        <ProfileCompletionScore
          photo={photo}
          gallery={gallery}
          bio={bio}
          interests={interests}
          location={location}
          preferences={preferences}
        />
        <PublicProfileShare publicProfile={publicProfile} onChange={togglePublicProfile} />
        <ReferralsCard />
        <div className="bg-fnl-purple/60 border border-purple-900/40 rounded-2xl p-5 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="boost-card">
          <div>
            <div className="text-white font-bold text-lg">Boost your profile</div>
            <div className="text-purple-300 text-xs">Skip the queue and appear at the top of browse for 30 minutes.</div>
          </div>
          <BoostButton />
        </div>
        <form onSubmit={save} className="bg-fnl-purple/60 border border-purple-900/40 rounded-2xl p-7 space-y-5">
          <div className="flex items-center gap-5">
            <div onClick={() => fileRef.current?.click()} className="relative w-24 h-24 rounded-full bg-fnl-purple-light border-2 border-dashed border-fnl-pink/60 flex items-center justify-center cursor-pointer overflow-hidden hover:border-fnl-pink">
              {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : <Camera className="w-8 h-8 text-fnl-pink" />}
            </div>
            <div>
              <p className="text-white text-sm font-medium">Profile Photo</p>
              <p className="text-purple-300 text-xs">JPG/PNG up to {MAX_PHOTO_SIZE_MB}MB — large photos auto-resized</p>
              <input type="file" accept="image/*" ref={fileRef} onChange={handlePhoto} className="hidden" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white mb-2 block">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white border-0 h-11" />
            </div>
            <div>
              <Label className="text-white mb-2 block">Age</Label>
              <Input type="number" min={MIN_AGE} max={MAX_AGE} value={age} onChange={(e) => setAge(e.target.value)} className="bg-white border-0 h-11" />
            </div>
          </div>
          <div>
            <Label className="text-white mb-2 block">Gender</Label>
            <RadioGroup value={gender} onValueChange={setGender} className="flex gap-4">
              {['female', 'male', 'other'].map((g) => (
                <div key={g} className="flex items-center gap-2">
                  <RadioGroupItem value={g} id={`p-${g}`} className="border-fnl-pink text-fnl-pink" />
                  <Label htmlFor={`p-${g}`} className="text-purple-100 capitalize cursor-pointer">{g}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label className="text-white mb-2 block">Status <span className="text-purple-300 text-xs font-normal">(your relationship status)</span></Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="status-options">
              {['single', 'married', 'divorced', 'other'].map((s) => {
                const active = relationshipStatus === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRelationshipStatus(active ? '' : s)}
                    data-testid={`status-${s}`}
                    className={
                      active
                        ? 'px-2 py-2 rounded-lg text-xs font-semibold capitalize bg-fnl-pink text-white border border-fnl-pink shadow-[0_4px_12px_rgba(238,42,123,0.35)] transition'
                        : 'px-2 py-2 rounded-lg text-xs font-medium capitalize bg-fnl-purple-light/40 text-purple-200 border border-purple-800/40 hover:border-fnl-pink hover:text-white transition'
                    }
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-white mb-2 block">Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} className="bg-white border-0 h-11" />
          </div>
          <div>
            <Label className="text-white mb-2 block">Bio</Label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} className="bg-white border-0" />
          </div>
          <div>
            <Label className="text-white mb-2 block">Interests</Label>
            <div className="flex gap-2">
              <Input value={interestInput} onChange={(e) => setInterestInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInterest(); } }} className="bg-white border-0 h-11 flex-1" />
              <Button type="button" onClick={addInterest} className="bg-fnl-pink hover:bg-fnl-pink h-11">Add</Button>
            </div>
            {interests.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {interests.map((i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-fnl-pink/20 text-fnl-pink text-sm border border-fnl-pink/40">
                    {i}<button type="button" onClick={() => removeInterest(i)} className="hover:text-white"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-white mb-2 block">Interest-Into <span className="text-purple-300 text-xs font-normal">(select what you're into)</span></Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {PREFERENCE_OPTIONS.map((p) => {
                const active = preferences.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPreferences(active ? preferences.filter((x) => x !== p) : [...preferences, p])}
                    className={
                      active
                        ? 'px-2 py-2 rounded-lg text-xs font-semibold bg-fnl-pink text-white border border-fnl-pink shadow-[0_4px_12px_rgba(238,42,123,0.35)] transition'
                        : 'px-2 py-2 rounded-lg text-xs font-medium bg-fnl-purple-light/40 text-purple-200 border border-purple-800/40 hover:border-fnl-pink hover:text-white transition'
                    }
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
          <GalleryEditor photos={gallery} onChange={setGallery} />
          <PushToggle />
          <Button type="submit" disabled={saving} className="w-full h-12 bg-fnl-pink hover:bg-fnl-pink text-white font-semibold btn-shadow-pink">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default MyProfile;
